import { Component, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { merge, Observable } from "rxjs";
import { map, scan, tap } from "rxjs/operators";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { KontaktpersonViewModel } from "~app/features/virkeskop/models/skogsvard/kontaktperson-models";
import { KundListItem } from "../../../virkeskop/models/skogsvard/kund-models";
import { KundMediatorService } from "../../../virkeskop/services/kund-mediator.service";
import { AvtalKundSearchModalComponent } from "../avtal-kund-search-modal/avtal-kund-search-modal.component";

type KundState = {
    kund: KundListItem;
    kontaktpersoner: KontaktpersonViewModel[];
    kontaktperson: KontaktpersonViewModel;
};

type KundReducer = (curr: KundState) => KundState;

@Component({
    selector: "rask-avtal-kund",
    imports: [RaskCommonModule],
    templateUrl: "./avtal-kund.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./avtal-kund.component.scss"
})
export class AvtalKundComponent implements OnInit {
    protected kontaktpersonControl = new FormControl<number>(null);
    protected kund$: Observable<KundState>;

    private readonly modalService = inject(NgbModal);
    private readonly kundMediatorService = inject(KundMediatorService);

    private readonly kontaktpersonerReducer: Observable<KundReducer> = this.kundMediatorService.kontaktpersoner$.pipe(
        map(kontaktpersoner => kontaktpersoner.map<KontaktpersonViewModel>(k => new KontaktpersonViewModel(k))),
        map(kontaktpersoner => (curr) => {
            const kontaktperson = this.getKontaktperson(curr.kund.model.kontaktpersonId, kontaktpersoner);

            if(kontaktperson) {
                this.kontaktpersonControl.patchValue(kontaktperson?.id, {
                    onlySelf:true,
                    emitEvent: kontaktperson?.id !== curr.kund.model.kontaktpersonId
                });
            }

            return {
                ...curr,
                kontaktpersoner,
                kontaktperson
            };
        })
    );

    private readonly kundChangeReducer: Observable<KundReducer> = this.kundMediatorService.kund$.pipe(
        map((kund) => (curr) => ({
            ...curr,
            kund
        }))
    );

    private readonly kontaktpersonChangeReducer: Observable<KundReducer> = this.kontaktpersonControl.valueChanges.pipe(
        tap(kontaktpersonId => this.kundMediatorService.updateKontaktperson(kontaktpersonId)),
        map(kontaktpersonId => (curr) => ({
            ...curr,
            kontaktperson: curr.kontaktpersoner?.find(k => k.id === kontaktpersonId)
        }))
    );

    ngOnInit(): void {
        this.kund$ = merge(
            this.kontaktpersonChangeReducer,
            this.kundChangeReducer,
            this.kontaktpersonerReducer
        ).pipe(
            scan((acc: KundState, reducer: KundReducer) => reducer(acc), {})
        );
    }

    protected async searchClick() {
        if (this.modalService.hasOpenModals()) {
            return;
        }

        const modalRef = this.modalService.open(AvtalKundSearchModalComponent, {
            keyboard: false,
            backdrop: "static",
            size: "xl"
        });
        try{
            const result: KundListItem = await modalRef.result;
            this.kundMediatorService.addKund(result);
        } catch{}
    }

    private getKontaktperson(kontaktpersonId: number, kontaktpersoner: KontaktpersonViewModel[]): KontaktpersonViewModel {
        if(kontaktpersonId) {
            return kontaktpersoner.find(k => k.model.id === kontaktpersonId);
        } else if(kontaktpersoner.length === 1) {
            return kontaktpersoner[0];
        }
        return null;
    }
}
